import { BotOnly } from '@/features/shared/components/BotOnly'
import { getCategoryUrl } from '@/features/cms/services/CMS/utils'
import { type ShoppingCategory } from '@/features/shop/services/ShoppingCategories/types'

type Props = {
  navCategories: ShoppingCategory[]
  storeName?: string
}

/**
 * Renders the retailer category navigation as plain anchor links that are
 * hidden from real users (via `BotOnly`) but present in the SSR HTML so that
 * SEO crawlers can discover every category, its children, and subcategories.
 *
 * Nameless categories are skipped at every level — an anchor with no text
 * gives crawlers no context (and no accessible name), which is bad for SEO.
 */
export const BotReadableCategoryNav = ({ navCategories, storeName }: Props) => (
  <BotOnly>
    <nav>
      <ul>
        {navCategories.map((category) =>
          category.name ? (
            <CategoryTreeItem key={category.id} category={category} storeName={storeName} />
          ) : null,
        )}
      </ul>
    </nav>
  </BotOnly>
)

/**
 * Recursively renders a category and its children as nested `<li>`/`<ul>`
 * elements, so the hierarchy is expressed structurally at any depth. Callers
 * pass only named categories, and named children are filtered the same way
 * before recursing.
 */
const CategoryTreeItem = ({
  category,
  storeName,
}: {
  category: ShoppingCategory
  storeName?: string
}) => {
  const childCategories = category.children?.filter((child) => child.name) ?? []

  return (
    <li>
      <a href={getCategoryUrl(category, storeName)}>{category.name}</a>
      {childCategories.length > 0 && (
        <ul>
          {childCategories.map((child) => (
            <CategoryTreeItem key={child.id} category={child} storeName={storeName} />
          ))}
        </ul>
      )}
    </li>
  )
}
